import Repository from "@/repository";
import { getCarNumberFieldValue } from "@/utils/carNumber";
import {
  fetchAdditionalTires,
  fetchAllTires,
  fetchStandardTires,
} from "@/utils/tires";
import {
  buildOfferWheelQuantityMap,
  getOffersTotalWheelQuantity,
  getOfferWheelQuantityByTire,
} from "@/utils/complexOffers";

export default {
  data() {
    return {
      makes: [],
      models: [],
      generations: [],
      standardTires: [],
      additionalTires: [],
      tireGroups: [],
      selectedTireId: null,
      isTirePickerOpen: false,
      tirePickerSize: "mm",
      tirePickerGroup: "",
      tirePickerCargo: false,
      pickerTiresCache: {},
      generationTiresCache: {},
      activeTireGroupDiameter: null,
      activePickerTireId: null,
      pickerPrimary: {
        width: "",
        profile: "",
        diameter: "",
      },
      pickerSecondary: {
        width: "",
        profile: "",
        diameter: "",
      },
      isWideTires: false,
      includeRunflat: false,
      includePressureSensor: false,
      includeAlloyWheel: false,
      makeSearchRequestId: 0,
      modelSearchRequestId: 0,
      generationSearchRequestId: 0,
      selectedCarIdx: null,
    };
  },
  created() {
    this.restoreAdditionalOptions();
  },
  methods: {
    ensureSeasonObjects() {
      const order = this.$store.state.orders.order;
      if (!order?.car?.tires) {
        this.$store.commit("orders/setOrderField", {
          field: "car.tires",
          value: {},
        });
      }
      const tires = this.$store.state.orders.order.car.tires;
      if (tires.use_same_tires === undefined) {
        this.$store.commit("orders/setOrderField", {
          field: "car.tires.use_same_tires",
          value: true,
        });
      }
      if (!tires.summer_tires) {
        this.$store.commit("orders/setOrderField", {
          field: "car.tires.summer_tires",
          value: {
            first_tires: null,
            is_alloy_wheel: false,
            is_assembled_tire: false,
            is_active: true,
          },
        });
      }
      if (!tires.winter_tires) {
        this.$store.commit("orders/setOrderField", {
          field: "car.tires.winter_tires",
          value: {
            first_tires: null,
            is_alloy_wheel: false,
            is_assembled_tire: false,
            is_active: false,
          },
        });
      }
    },
    normalizeEntityId(value) {
      if (!value) return null;
      return typeof value === "object" ? value.id || null : value;
    },
    getSortedStandardTires(tires = []) {
      return [...(Array.isArray(tires) ? tires : [])].sort((first, second) => {
        const firstParsed = this.parseMetricTireTitle(this.getTireTitle(first));
        const secondParsed = this.parseMetricTireTitle(this.getTireTitle(second));

        if (!firstParsed || !secondParsed) {
          return this.getTireTitle(first).localeCompare(this.getTireTitle(second), "ru");
        }

        return (
          Number(firstParsed.diameter) - Number(secondParsed.diameter) ||
          Number(firstParsed.width) - Number(secondParsed.width) ||
          Number(firstParsed.profile) - Number(secondParsed.profile)
        );
      });
    },
    getTireTitle(tire) {
      if (!tire) return "";
      return typeof tire === "object" ? tire.title || "" : "";
    },
    async findGenerationTireById(generationId, tireId) {
      const normalizedTireId = this.normalizeEntityId(tireId);
      if (!generationId || !normalizedTireId) {
        return null;
      }

      const cacheKey = String(generationId);
      if (!this.generationTiresCache[cacheKey]) {
        const { tires } = await fetchAllTires(generationId);
        this.generationTiresCache = {
          ...this.generationTiresCache,
          [cacheKey]: Array.isArray(tires) ? tires : [],
        };
      }

      return (
        this.generationTiresCache[cacheKey].find(
          (item) => this.normalizeEntityId(item) === normalizedTireId,
        ) || null
      );
    },
    async resolveClientCarTireEntity(carItem, tire) {
      if (!tire) {
        return null;
      }
      if (typeof tire === "object" && tire.title) {
        return tire;
      }

      const generationId =
        this.normalizeEntityId(carItem?.generation) ||
        this.$store.state.orders.order?.car?.generation;

      return this.findGenerationTireById(generationId, tire);
    },
    parseMetricTireTitle(title) {
      const match = String(title || "")
        .trim()
        .replace(/\s+/g, "")
        .match(/^(\d{3})\/(\d{2,3})R(\d{2})$/i);
      if (!match) return null;
      return {
        width: match[1],
        profile: match[2],
        diameter: match[3],
      };
    },
    getParsedPickerTires() {
      return (this.tireGroups || []).flatMap((group) =>
        (group.tires || [])
          .map((tire) => {
            const parsed = this.parseMetricTireTitle(tire.title);
            if (!parsed) return null;
            return {
              ...parsed,
              tire,
            };
          })
          .filter(Boolean),
      );
    },
    getSelectionFromTire(tire) {
      return this.parseMetricTireTitle(this.getTireTitle(tire)) || {
        width: "",
        profile: "",
        diameter: "",
      };
    },
    findPickerTire(selection) {
      const { width, profile, diameter } = selection || {};
      if (!width || !profile || !diameter) return null;
      return (
        this.getParsedPickerTires().find(
          (item) =>
            item.width === String(width) &&
            item.profile === String(profile) &&
            item.diameter === String(diameter),
        )?.tire || null
      );
    },
    setPickerSelection(target, patch) {
      this[target] = {
        ...this[target],
        ...patch,
      };
    },
    syncPickerStateFromOrder() {
      const orderTires = this.$store.state.orders.order?.car?.tires || {};
      const primaryTire =
        this.findPickerTire(
          this.getSelectionFromTire(
            [...(this.standardTires || []), ...(this.additionalTires || [])].find(
              (item) => item.id === this.selectedTireId,
            ) ||
              this.getParsedPickerTires().find(
                (item) => item.tire.id === this.selectedTireId,
              )?.tire,
          ),
        ) || null;
      const primarySelection = primaryTire
        ? this.getSelectionFromTire(primaryTire)
        : { width: "", profile: "", diameter: "" };
      this.pickerPrimary = primarySelection;

      const secondTireId =
        orderTires?.summer_tires?.second_tires || orderTires?.second_tires || null;
      const secondTire = this.getParsedPickerTires().find(
        (item) => item.tire.id === this.normalizeEntityId(secondTireId),
      )?.tire;
      this.pickerSecondary = secondTire
        ? this.getSelectionFromTire(secondTire)
        : { width: "", profile: "", diameter: "" };

      this.isWideTires = Boolean(this.normalizeEntityId(secondTireId));
      this.includeAlloyWheel = Boolean(
        this.$store.state.orders.offerSettings?.include_alloy_wheel,
      );
      this.includeRunflat = Boolean(
        this.$store.state.orders.offerSettings?.include_runflat,
      );
      this.includePressureSensor = Boolean(
        this.$store.state.orders.offerSettings?.include_pressure_sensor,
      );
    },
    applyTireToOrder(tire) {
      if (!tire) return;
      this.ensureSeasonObjects();
      const tireId = this.normalizeEntityId(tire);
      const tireTitle = this.getTireTitle(tire);

      this.$store.commit("orders/setOrderField", {
        field: "car.tires.summer_tires.first_tires",
        value: tireId,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.first_tires",
        value: tireId,
      });
      this.$store.commit("orders/setCarField", {
        field: "first_tires",
        value: tireTitle,
      });
      this.selectedTireId = tireId;
    },
    clearTires() {
      this.ensureSeasonObjects();
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.summer_tires.first_tires",
        value: null,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.first_tires",
        value: null,
      });
      this.$store.commit("orders/setCarField", {
        field: "first_tires",
        value: "",
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.summer_tires.second_tires",
        value: null,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.second_tires",
        value: null,
      });
      this.$store.commit("orders/setCarField", {
        field: "second_tires",
        value: "",
      });
      this.selectedTireId = null;
      this.standardTires = [];
      this.additionalTires = [];
      this.tireGroups = [];
      this.pickerTiresCache = {};
      this.activeTireGroupDiameter = null;
      this.activePickerTireId = null;
      this.pickerPrimary = { width: "", profile: "", diameter: "" };
      this.pickerSecondary = { width: "", profile: "", diameter: "" };
      this.isWideTires = false;
    },
    resetSecondaryTireState() {
      this.ensureSeasonObjects();
      this.isWideTires = false;
      this.pickerSecondary = { width: "", profile: "", diameter: "" };
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.use_same_tires",
        value: true,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.summer_tires.second_tires",
        value: null,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.second_tires",
        value: null,
      });
      this.$store.commit("orders/setCarField", {
        field: "second_tires",
        value: "",
      });
    },
    hasClientCarTires(tireConfig) {
      return Boolean(
        this.normalizeEntityId(tireConfig?.summer_tires?.first_tires) ||
          this.normalizeEntityId(tireConfig?.winter_tires?.first_tires) ||
          this.normalizeEntityId(tireConfig?.first_tires),
      );
    },
    hasActiveClientCarSeason(tireConfig) {
      return (
        tireConfig?.is_active === true ||
        tireConfig?.summer_tires?.is_active === true ||
        tireConfig?.winter_tires?.is_active === true
      );
    },
    getClientCarActiveSeasonSource(tireConfig) {
      if (!tireConfig) {
        return null;
      }

      if (tireConfig?.summer_tires?.is_active === true) {
        return tireConfig.summer_tires;
      }

      if (tireConfig?.winter_tires?.is_active === true) {
        return tireConfig.winter_tires;
      }

      return tireConfig?.summer_tires || tireConfig?.winter_tires || tireConfig;
    },
    getClientCarTireConfig(carItem) {
      const tires = (Array.isArray(carItem?.tires) ? carItem.tires : []).filter((item) =>
        this.hasClientCarTires(item),
      );
      if (!tires.length) {
        return null;
      }

      return tires.find((item) => this.hasActiveClientCarSeason(item)) || tires[0];
    },
    getClientCarPrimaryTire(carItem) {
      const tireConfig = this.getClientCarTireConfig(carItem);
      const activeSeasonSource = this.getClientCarActiveSeasonSource(tireConfig);
      return (
        activeSeasonSource?.first_tires ||
        tireConfig?.first_tires ||
        null
      );
    },
    getClientCarSecondaryTire(carItem) {
      const tireConfig = this.getClientCarTireConfig(carItem);
      const activeSeasonSource = this.getClientCarActiveSeasonSource(tireConfig);
      return (
        activeSeasonSource?.second_tires ||
        tireConfig?.second_tires ||
        null
      );
    },
    getClientCarUseSameTires(carItem) {
      const tireConfig = this.getClientCarTireConfig(carItem);
      const activeSeasonSource = this.getClientCarActiveSeasonSource(tireConfig);
      const secondaryTire = this.getClientCarSecondaryTire(carItem);
      if (!secondaryTire) {
        return true;
      }
      if (tireConfig?.use_same_tires !== undefined) {
        return Boolean(tireConfig.use_same_tires);
      }
      if (activeSeasonSource?.use_same_tires !== undefined) {
        return Boolean(activeSeasonSource.use_same_tires);
      }
      return !secondaryTire;
    },
    getClientCarOfferSettings(carItem) {
      const tireConfig = this.getClientCarTireConfig(carItem);
      const activeSeasonSource = this.getClientCarActiveSeasonSource(tireConfig);
      const sources = [
        tireConfig,
        activeSeasonSource,
      ].filter(Boolean);

      const hasEnabledFlag = (keys) =>
        sources.some((source) => keys.some((key) => Boolean(source?.[key])));

      return {
        include_alloy_wheel: hasEnabledFlag([
          "include_alloy_wheel",
          "is_alloy_wheel",
        ]),
        include_runflat: hasEnabledFlag([
          "include_runflat",
          "is_runflat",
          "runflat",
        ]),
        include_pressure_sensor: hasEnabledFlag([
          "include_pressure_sensor",
          "is_pressure_sensor",
          "pressure_sensor",
          "has_pressure_sensor",
        ]),
      };
    },
    syncClientCarOfferSettings(carItem) {
      const offerSettings = this.getClientCarOfferSettings(carItem);
      this.includeAlloyWheel = Boolean(offerSettings.include_alloy_wheel);
      this.includeRunflat = Boolean(offerSettings.include_runflat);
      this.includePressureSensor = Boolean(
        offerSettings.include_pressure_sensor
      );
      this.$store.commit("orders/setOfferSettings", {
        ...offerSettings,
        touched: false,
      });
    },
    restoreAdditionalOptions() {
      const offerSettings = this.$store.state.orders.offerSettings || {};
      this.includeAlloyWheel = Boolean(offerSettings.include_alloy_wheel);
      this.includeRunflat = Boolean(offerSettings.include_runflat);
      this.includePressureSensor = Boolean(
        offerSettings.include_pressure_sensor
      );
    },
    isCurrentClientCar(carItem) {
      const orderCar = this.$store.state.orders.order?.car || {};
      return (
        this.normalizeEntityId(orderCar.make) ===
          this.normalizeEntityId(carItem?.make) &&
        this.normalizeEntityId(orderCar.model) ===
          this.normalizeEntityId(carItem?.model) &&
        this.normalizeEntityId(orderCar.generation) ===
          this.normalizeEntityId(carItem?.generation)
      );
    },
    async applyClientCarTires(carItem) {
      const primaryTire = await this.resolveClientCarTireEntity(
        carItem,
        this.getClientCarPrimaryTire(carItem),
      );
      if (!primaryTire) {
        return;
      }

      const secondaryTire = await this.resolveClientCarTireEntity(
        carItem,
        this.getClientCarSecondaryTire(carItem),
      );
      const primaryTireId = this.normalizeEntityId(primaryTire);
      const secondaryTireId = this.normalizeEntityId(secondaryTire);
      const useSameTires = this.getClientCarUseSameTires(carItem);
      const hasSecondaryTire = Boolean(secondaryTireId);

      this.applyTireToOrder(primaryTire);
      this.ensureSeasonObjects();
      this.activePickerTireId = primaryTireId;
      this.isWideTires = hasSecondaryTire;

      this.$store.commit("orders/setOrderField", {
        field: "car.tires.use_same_tires",
        value: useSameTires,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.summer_tires.second_tires",
        value: secondaryTireId || null,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.second_tires",
        value: secondaryTireId || null,
      });
      this.$store.commit("orders/setCarField", {
        field: "second_tires",
        value: this.getTireTitle(secondaryTire),
      });

      await this.$store.dispatch("orders/saveOrder");
      await this.refreshComplexesAndPersistOfferSettings({
        tireId: primaryTireId,
        secondTireId: secondaryTireId || null,
      });
    },
    setPickerGroups(groups) {
      this.tireGroups = Array.isArray(groups) ? groups : [];
      const selectedGroup = this.tireGroups.find((group) =>
        (group.tires || []).some(
          (item) => item.id === (this.activePickerTireId || this.selectedTireId),
        ),
      );
      const hasCurrentGroup = this.tireGroups.some(
        (item) => String(item.diameter) === String(this.activeTireGroupDiameter),
      );
      if (selectedGroup) {
        this.activeTireGroupDiameter = selectedGroup.diameter;
      } else if (!hasCurrentGroup) {
        this.activeTireGroupDiameter = this.tireGroups[0]?.diameter ?? null;
      }
      if (this.selectedTireId) {
        this.activePickerTireId = this.selectedTireId;
      } else if (!this.activePickerTireId) {
        this.activePickerTireId = this.tireGroups[0]?.tires?.[0]?.id ?? null;
      }
      this.syncPickerStateFromOrder();
    },
    getPickerTiresCacheKey() {
      const generationId = this.$store.state.orders.order.car?.generation;
      if (!generationId) {
        return "";
      }

      return JSON.stringify({
        generationId,
        size: this.tirePickerSize,
        group: this.tirePickerGroup || "",
        isCargo: Boolean(this.tirePickerCargo),
      });
    },
    async fetchPickerTires() {
      const generationId = this.$store.state.orders.order.car?.generation;
      if (!generationId) {
        this.tireGroups = [];
        return;
      }
      const cacheKey = this.getPickerTiresCacheKey();
      if (cacheKey && this.pickerTiresCache[cacheKey]) {
        this.setPickerGroups(this.pickerTiresCache[cacheKey]);
        return;
      }

      const params = {
        is_cargo: this.tirePickerCargo,
      };
      if (this.tirePickerGroup) {
        params.group = this.tirePickerGroup;
        params.generation = generationId;
      }

      const { value } = await Repository.Tires.getSizes({
        size: this.tirePickerSize,
        params,
      });
      if (cacheKey) {
        this.pickerTiresCache = {
          ...this.pickerTiresCache,
          [cacheKey]: value,
        };
      }
      this.setPickerGroups(value);
    },
    setTirePickerGroup(value) {
      if (this.tirePickerGroup === value) return;
      this.tirePickerGroup = value;
      this.fetchPickerTires();
    },
    setTirePickerSize(value) {
      if (this.tirePickerSize === value) return;
      this.tirePickerSize = value;
      this.fetchPickerTires();
    },
    toggleTirePickerCargo() {
      this.tirePickerCargo = !this.tirePickerCargo;
      this.fetchPickerTires();
    },
    setActiveTireGroup(group) {
      this.activeTireGroupDiameter = group?.diameter ?? null;
    },
    setActivePickerTire(tire) {
      this.activePickerTireId = tire?.id ?? null;
    },
    closeTirePicker() {
      this.isTirePickerOpen = false;
      this.activePickerTireId = this.selectedTireId;
    },
    async toggleWideTires() {
      this.isWideTires = !this.isWideTires;
      if (!this.isWideTires) {
        this.resetSecondaryTireState();

        await this.$store.dispatch("orders/saveOrder");

        const primaryTireId =
          this.selectedTireId ||
          this.$store.state.orders.order?.car?.tires?.summer_tires?.first_tires ||
          this.$store.state.orders.order?.car?.tires?.first_tires ||
          this.normalizeEntityId(this.selectedPrimaryTire);

        if (primaryTireId) {
          await this.refreshComplexesAndPersistOfferSettings({
            tireId: this.normalizeEntityId(primaryTireId),
            secondTireId: null,
          });
        }
      }
    },
    getSelectedComplexFromStore() {
      const selectedComplexId = this.$store.state.orders.order?.complex;
      if (!selectedComplexId) {
        return null;
      }

      const categories = this.$store.state.complexes.complexes || [];
      return categories
        .flatMap((category) => category.complexes || [])
        .find((complex) => String(complex.id) === String(selectedComplexId));
    },
    buildSelectedComplexOfferPayload(quantity = null) {
      const complex = this.getSelectedComplexFromStore();
      if (!complex) {
        return [];
      }

      const complexOfferIds = (complex.offers || [])
        .map((item) => this.normalizeEntityId(item.offer))
        .filter(Boolean);

      if (!complexOfferIds.length) {
        return [];
      }

      const orderOffers = this.$store.state.orders.order?.offers || [];
      const selectedOrderOffers = orderOffers.filter((item) =>
        complexOfferIds.includes(this.normalizeEntityId(item.offer)),
      );
      const currentQty =
        (getOffersTotalWheelQuantity(selectedOrderOffers) || complex.qty) ??
        complex.offers?.[0]?.default_wheels_quan ??
        1;
      const wheelsQuan = Math.max(1, Number(quantity ?? (currentQty || 1)));
      const quantityMap = buildOfferWheelQuantityMap(
        selectedOrderOffers,
        wheelsQuan,
      );

      return selectedOrderOffers
        .map((item) => {
          const offerId = this.normalizeEntityId(item.offer);
          const complexOffer = (complex?.offers || []).find(
            (entry) => this.normalizeEntityId(entry.offer) === offerId,
          );
          const payload = {
            id: item.id,
            offer: offerId,
            tire: this.normalizeEntityId(item.tire),
            wheels_quan: getOfferWheelQuantityByTire(
              item,
              quantityMap,
              selectedOrderOffers.indexOf(item),
            ),
          };

          if (complexOffer?.offer?.allow_runflat) {
            payload.include_runflat = this.includeRunflat;
          }

          if (complexOffer?.offer?.allow_pressure_sensor) {
            payload.include_pressure_sensor = this.includePressureSensor;
          }

          if (complexOffer?.offer?.allow_alloy_wheel) {
            payload.include_alloy_wheel = this.includeAlloyWheel;
          }

          return payload;
        })
        .filter((item) => item.id && item.offer && item.tire);
    },
    async patchSelectedComplexOffers(quantity = null) {
      const payload = this.buildSelectedComplexOfferPayload(quantity);
      if (!payload.length) {
        return { value: [], error: null };
      }

      return this.$store.dispatch("orders/patchOffers", { payload });
    },
    async refreshComplexesAndPersistOfferSettings({
      tireId,
      secondTireId = null,
    }) {
      if (!tireId) {
        return;
      }

      await this.$store.dispatch("complexes/fetchTiresComplexes", {
        tireId,
        secondTireId,
      });
      await this.persistAdditionalOptions();
    },
    async persistAdditionalOptions() {
      this.$store.commit("orders/setOfferSettings", {
        include_alloy_wheel: this.includeAlloyWheel,
        include_runflat: this.includeRunflat,
        include_pressure_sensor: this.includePressureSensor,
        touched: true,
      });

      return this.patchSelectedComplexOffers();
    },
    async toggleRunflat() {
      this.includeRunflat = !this.includeRunflat;
      await this.persistAdditionalOptions();
    },
    async togglePressureSensor() {
      this.includePressureSensor = !this.includePressureSensor;
      await this.persistAdditionalOptions();
    },
    async toggleAlloyWheel() {
      this.includeAlloyWheel = !this.includeAlloyWheel;
      await this.persistAdditionalOptions();
    },
    async searchMakes() {
      const car = this.$store.state.orders.car;
      const requestId = ++this.makeSearchRequestId;
      const requestedTitle = String(car.make || "").trim();
      const params = requestedTitle ? { title: requestedTitle } : {};
      const { value, error } = await Repository.Makes.get(params);
      if (requestId !== this.makeSearchRequestId) return;
      if (!error && requestedTitle === String(car.make || "").trim()) {
        this.makes = value;
      }
    },
    resetMakes() {
      setTimeout(() => {
        this.makes = [];
      }, 300);
    },
    clearMake() {
      this.$store.commit("orders/setOrderField", {
        field: "car.make",
        value: null,
      });
      this.$store.commit("orders/setCarField", { field: "make", value: "" });
      this.makes = [];
      this.clearModel();
    },
    async searchModels({ autoSelectSingle = true } = {}) {
      const order = this.$store.state.orders.order;
      const car = this.$store.state.orders.car;
      if (!order.car.make) {
        this.models = [];
        return false;
      }
      const requestId = ++this.modelSearchRequestId;
      const requestedTitle = String(car.model || "").trim();
      const requestedMakeId = order.car.make;
      const params = requestedTitle ? { title: requestedTitle } : {};
      const { value, error } = await Repository.Makes.Models.get({
        makeId: requestedMakeId,
        params,
      });
      if (requestId !== this.modelSearchRequestId) return;
      if (error) {
        return false;
      }
      if (
        requestedTitle !== String(car.model || "").trim() ||
        requestedMakeId !== order.car.make
      ) {
        return false;
      }
      const models = Array.isArray(value) ? value : [];
      const shouldAutoSelect =
        autoSelectSingle &&
        models.length === 1 &&
        !requestedTitle &&
        !this.$store.state.orders.order.car?.model;

      if (shouldAutoSelect) {
        this.models = [];
        await this.setModel(models[0], { allowAutoSelectNext: true });
        return true;
      }

      this.models = models;
      return false;
    },
    async setModel(model, { allowAutoSelectNext = false } = {}) {
      this.$store.commit("orders/setOrderField", {
        field: "car.model",
        value: model.id,
      });
      this.$store.commit("orders/setCarField", {
        field: "model",
        value: model.title,
      });
      this.models = [];
      this.clearGeneration();
      if (allowAutoSelectNext) {
        return this.searchGenerations({ autoSelectSingle: true });
      }
      return false;
    },
    resetModels() {
      setTimeout(() => {
        this.models = [];
      }, 300);
    },
    clearModel() {
      this.$store.commit("orders/setOrderField", {
        field: "car.model",
        value: null,
      });
      this.$store.commit("orders/setCarField", { field: "model", value: "" });
      this.models = [];
      this.clearGeneration();
    },
    async searchGenerations({ autoSelectSingle = true } = {}) {
      const order = this.$store.state.orders.order;
      const car = this.$store.state.orders.car;
      if (!order.car.make || !order.car.model) {
        this.generations = [];
        return false;
      }
      const requestId = ++this.generationSearchRequestId;
      const requestedTitle = String(car.generation || "").trim();
      const params = requestedTitle ? { title: requestedTitle } : {};
      const { value, error } = await Repository.Makes.Models.Generations.get({
        makeId: order.car.make,
        modelId: order.car.model,
        params,
      });
      if (requestId !== this.generationSearchRequestId) return;
      if (error) {
        return false;
      }
      if (
        requestedTitle !== String(car.generation || "").trim() ||
        !order.car.make ||
        !order.car.model
      ) {
        return false;
      }
      const generations = Array.isArray(value) ? value : [];
      const shouldAutoSelect =
        autoSelectSingle &&
        generations.length === 1 &&
        !requestedTitle &&
        !this.$store.state.orders.order.car?.generation;

      if (shouldAutoSelect) {
        this.generations = [];
        await this.setGeneration(generations[0]);
        return true;
      }

      this.generations = generations;
      return false;
    },
    async setGeneration(generation) {
      this.$store.commit("orders/setOrderField", {
        field: "car.generation",
        value: generation.id,
      });
      this.$store.commit("orders/setCarField", {
        field: "generation",
        value: generation.title,
      });
      this.generations = [];
      await this.loadStandardTires(generation.id);
    },
    resetGenerations() {
      setTimeout(() => {
        this.generations = [];
      }, 300);
    },
    clearGeneration() {
      this.$store.commit("orders/setOrderField", {
        field: "car.generation",
        value: null,
      });
      this.$store.commit("orders/setCarField", {
        field: "generation",
        value: "",
      });
      this.generations = [];
      this.clearTires();
    },
    async ensureStandardTires(generationId) {
      const resolvedGenerationId =
        generationId || this.$store.state.orders.order?.car?.generation;
      if (!resolvedGenerationId) {
        return;
      }

      const requests = [];
      if (!this.standardTires.length) {
        requests.push(
          fetchStandardTires(resolvedGenerationId).then((tires) => {
            this.standardTires = this.getSortedStandardTires(tires);
          }),
        );
      }
      if (!this.additionalTires.length) {
        requests.push(
          fetchAdditionalTires(resolvedGenerationId).then((tires) => {
            this.additionalTires = this.getSortedStandardTires(tires);
          }),
        );
      }

      await Promise.all(requests);
    },
    async loadStandardTires(generationId) {
      const [standardTires, additionalTires] = await Promise.all([
        fetchStandardTires(generationId),
        fetchAdditionalTires(generationId),
      ]);
      const tires = this.getSortedStandardTires(standardTires);
      this.standardTires = tires;
      this.additionalTires = this.getSortedStandardTires(additionalTires);
      this.isTirePickerOpen = false;
      this.tireGroups = [];
      this.activeTireGroupDiameter = null;

      const currentTireId =
        this.$store.state.orders.order?.car?.tires?.summer_tires?.first_tires ||
        this.$store.state.orders.order?.car?.tires?.first_tires ||
        this.selectedTireId;

      const currentStandardTire =
        tires.find(
          (item) => this.normalizeEntityId(item) === this.normalizeEntityId(currentTireId),
        ) || null;
      const defaultStandardTire = currentStandardTire || tires[0] || null;

      if (!defaultStandardTire) {
        this.selectedTireId = null;
        this.activePickerTireId = null;
        return;
      }

      const defaultTireId = this.normalizeEntityId(defaultStandardTire);
      this.selectedTireId = defaultTireId;
      this.activePickerTireId = defaultTireId;

      if (currentStandardTire) {
        this.$store.commit("orders/setCarField", {
          field: "first_tires",
          value: this.getTireTitle(currentStandardTire),
        });
        return;
      }

      await this.selectTire(defaultStandardTire);
    },
    async openTirePicker() {
      if (this.isTirePickerOpen) return;
      this.isTirePickerOpen = true;
      await this.ensureStandardTires();
      await this.fetchPickerTires();
    },
    async confirmTirePicker({ closePicker = true } = {}) {
      const primaryTire = this.findPickerTire(this.pickerPrimary);
      if (!primaryTire) return;

      const secondaryTire = this.isWideTires
        ? this.findPickerTire(this.pickerSecondary)
        : null;

      this.applyTireToOrder(primaryTire);
      this.ensureSeasonObjects();

      const primaryTireId = this.normalizeEntityId(primaryTire);
      const secondaryTireId = this.normalizeEntityId(secondaryTire);

      this.$store.commit("orders/setOrderField", {
        field: "car.tires.use_same_tires",
        value: !this.isWideTires,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.summer_tires.second_tires",
        value: secondaryTireId || null,
      });
      this.$store.commit("orders/setOrderField", {
        field: "car.tires.second_tires",
        value: secondaryTireId || null,
      });
      this.$store.commit("orders/setCarField", {
        field: "second_tires",
        value: secondaryTire ? this.getTireTitle(secondaryTire) : "",
      });
      this.$store.commit("orders/setOfferSettings", {
        include_alloy_wheel: this.includeAlloyWheel,
        include_runflat: this.includeRunflat,
        include_pressure_sensor: this.includePressureSensor,
      });

      await this.$store.dispatch("orders/saveOrder");
      if (primaryTireId) {
        await this.refreshComplexesAndPersistOfferSettings({
          tireId: primaryTireId,
          secondTireId: secondaryTireId || null,
        });
      }
      if (closePicker) {
        this.isTirePickerOpen = false;
      }
    },
    async selectTire(tire) {
      this.applyTireToOrder(tire);
      this.resetSecondaryTireState();
      await this.$store.dispatch("orders/saveOrder");
      const tireId = this.normalizeEntityId(tire);
      if (tireId) {
        await this.refreshComplexesAndPersistOfferSettings({
          tireId,
        });
      }
    },
    hasSelectedVehicleData() {
      const orderCar = this.$store.state.orders.order?.car || {};
      return Boolean(
        orderCar.make ||
        orderCar.model ||
        orderCar.generation ||
        orderCar.tires?.summer_tires?.first_tires ||
        orderCar.tires?.first_tires ||
        this.selectedCarIdx !== null,
      );
    },
    async applyClientCar(carItem, idx = null) {
      if (!carItem) return;
      const keepSelectedOptions =
        this.isCurrentClientCar(carItem) &&
        Boolean(this.$store.state.orders.offerSettings?.touched);
      this.selectedCarIdx = idx;
      if (keepSelectedOptions) {
        this.restoreAdditionalOptions();
      } else {
        this.syncClientCarOfferSettings(carItem);
      }
      const hasOrderId = Boolean(this.$store.state.orders.order?.id);

      const nextNumber = getCarNumberFieldValue(carItem.number);

      this.$store.commit("orders/setOrderField", {
        field: "car.number",
        value: nextNumber,
      });

      if (carItem.make) {
        await this.setMake(carItem.make, {
          allowAutoSelectNext: !carItem.model,
        });
      } else {
        this.clearMake();
      }

      if (carItem.model) {
        await this.setModel(carItem.model, {
          allowAutoSelectNext: !carItem.generation,
        });
      }

      if (carItem.generation) {
        await this.setGeneration(carItem.generation);
      }

      if (this.$store.state.orders.order?.car?.generation) {
        await this.applyClientCarTires(carItem);
      }

      if (hasOrderId) {
        await this.$store.dispatch("orders/saveOrder");
      }
    },
    async setMake(make, { allowAutoSelectNext = false } = {}) {
      this.$store.commit("orders/setOrderField", {
        field: "car.make",
        value: make.id,
      });
      this.$store.commit("orders/setCarField", {
        field: "make",
        value: make.title,
      });
      this.makes = [];
      this.clearModel();
      if (allowAutoSelectNext) {
        return this.searchModels({ autoSelectSingle: true });
      }
      return false;
    },
    getClientCarBrands(client = this.client) {
      const cars = this.getActiveClientCars?.(client) || [];
      const brandCounts = cars.reduce((result, car) => {
        const title = car.make?.title || "";
        if (!title) return result;
        result[title] = (result[title] || 0) + 1;
        return result;
      }, {});

      return cars.reduce((brands, car, index) => {
        const title = car.make?.title;
        if (!title) return brands;

        const details = [car.model?.title, car.number].filter(Boolean).join(" ");
        const label =
          brandCounts[title] > 1 && details ? `${title} ${details}` : title;

        brands.push({ title: label, carIndex: index, car });
        return brands;
      }, []);
    },
  },
};
